import type { SidebarResult } from "../workers/dispatch/types";

export type CardSlotRefs = {
  assigneeEl: HTMLElement | null;
  etaEl: HTMLElement | null;
  statusEl: HTMLElement | null;
};

function computeStructureKey(result: SidebarResult): string {
  if (result.type === "flat") {
    return result.orders.map((o) => o.orderId).join(",");
  }
  if (result.type === "connections-drivers") {
    return result.workspaces
      .flatMap((ws) =>
        (ws.assignees ?? []).flatMap((a) => a.orders.map((o) => o.orderId)),
      )
      .join(",");
  }
  if (result.type === "connections") {
    return result.workspaces
      .flatMap((ws) => (ws.orders ?? []).map((o) => o.orderId))
      .join(",");
  }
  if (result.type === "locations") {
    return JSON.stringify({
      groups: result.groups.map((group) => ({
        locationName: group.locationName,
        orderIds: group.orders.map((order) => order.orderId),
      })),
      type: result.type,
    });
  }
  if (result.type === "states") {
    return result.groups
      .flatMap((g) => g.orders.map((o) => o.orderId))
      .join(",");
  }
  return "";
}

class SidebarRenderer {
  // Called from renderLoop when worker sends UPDATE_SIDEBAR
  applySidebarResult(result: SidebarResult): void {
    this.pending = result;

    // Only notify React subscribers when group structure (order IDs) changes
    const key = computeStructureKey(result);
    if (key !== this.structureKey) {
      this.structureKey = key;
      this.structureSnapshot = result;
      this.listeners.forEach((cb) => cb());
    }
  }

  getSnapshot = (): SidebarResult | null => this.structureSnapshot;

  private listeners = new Set<() => void>();
  private pending: SidebarResult | null = null;

  // Called by SidebarOrderCard on mount
  register(orderId: string, refs: CardSlotRefs): void {
    this.slots.set(orderId, refs);
  }

  private slots = new Map<string, CardSlotRefs>();
  private structureKey = "";
  private structureSnapshot: SidebarResult | null = null;

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  // Called once per RAF frame by renderLoop
  tick(): void {
    if (!this.pending) return;
    const result = this.pending;
    this.pending = null;

    const orderEntries =
      result.type === "flat"
        ? result.orders
        : result.type === "connections"
          ? result.workspaces.flatMap((ws) => ws.orders ?? [])
          : result.type === "connections-drivers"
            ? result.workspaces.flatMap((ws) =>
                (ws.assignees ?? []).flatMap((a) => a.orders),
              )
            : result.groups.flatMap((g) => g.orders);

    for (const entry of orderEntries) {
      const refs = this.slots.get(entry.orderId);
      if (!refs) continue;

      if (refs.statusEl) {
        refs.statusEl.textContent = entry.statusText;
        refs.statusEl.dataset.status = entry.status;
      }
      if (refs.assigneeEl) {
        refs.assigneeEl.textContent = entry.assigneeId ?? "Unassigned";
      }
    }
  }

  // Called by SidebarOrderCard on unmount
  unregister(orderId: string): void {
    this.slots.delete(orderId);
  }
}

export const sidebarRenderer = new SidebarRenderer();
